import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import ReactSelect from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import AsyncSelect from 'react-select/async';

import { isReactComponent } from '~utils/functions';

/**
 * Input attributes that are not lowercased, but still allowed.
 *
 * Docs: https://react.dev/reference/react-dom/components/input
 */
const INPUT_ALLOWED_ATTRIBUTES = ['readOnly'];

function MaterialWrapper(WrappedComponent, options = {}) {
	const { type = 'input' } = options;
	class WrappedMaterialComponent extends React.Component {
		constructor(props) {
			super(props);
			this.state = {
				isInFocus: false,
				shake: false,
				isEmpty: true,
				additionalProps: {},
				wrapperInFocus: false,
			};
			if (options.type === 'DATE_RANGE') {
				this.state.isEmpty = false;
			}
			this.inputControl = this.props.forwardedRef || React.createRef();
		}

		focus() {
			if (this.inputControl) {
				this.inputControl.focus();
			}
		}

		// Auto-grow for textarea
		autoGrow() {
			if (!this.props.autoGrow) {
				return;
			}

			if (WrappedComponent !== 'textarea') {
				return;
			}

			const el = this.inputControl?.current;

			if (!el) {
				return;
			}

			el.style.height = 'auto';
			el.style.maxHeight = 'none';
			el.style.height = `${el.scrollHeight}px`;
		}

		componentDidMount() {
			this.checkAndSetIsEmpty();
			if (this.props.autoFocus) {
				requestAnimationFrame(() => {
					this.inputControl.current.focus();
				});
			}
			if (
				WrappedComponent.prototype === ReactSelect.prototype ||
				WrappedComponent.prototype === AsyncSelect.prototype ||
				WrappedComponent.prototype === AsyncPaginate.prototype
			) {
				let className = `material-react-select ${
					this.props.smallHeight && 'small'
				}`;
				this.setState({
					additionalProps: {
						classNamePrefix: 'material-react-select',
						className,
					},
				});
			}

			// Auto-grow on mount for textarea
			if (WrappedComponent === 'textarea' && this.props.autoGrow) {
				this.autoGrow();
			}
		}

		componentDidUpdate(prevProps) {
			if (this.props.value !== prevProps.value) {
				this.checkAndSetIsEmpty();

				if (WrappedComponent === 'textarea' && this.props.autoGrow) {
					this.autoGrow();
				}
			}
		}

		checkAndSetIsEmpty() {
			if ((this.props.defaultValue || this.props.value) && this.state.isEmpty) {
				this.setState({
					isEmpty: false,
				});
			} else if (this.props.value === '') {
				this.setState({
					isEmpty: true,
				});
			}
		}

		handleChange = (evt) => {
			// Passing on the event to parents handler
			if (this.props.onChange) {
				this.props.onChange(evt);
			}

			if (WrappedComponent === 'textarea' && this.props.autoGrow) {
				this.autoGrow();
			}

			let isReactSelect = WrappedComponent.prototype === ReactSelect.prototype;

			if (_.get(evt, 'target.value') || (isReactSelect && evt)) {
				this.setState({
					isEmpty: false,
				});
			} else {
				this.setState({
					isEmpty: true,
				});
			}
		};

		handleKeyPress = (evt) => {
			if (this.props.onKeyPress) {
				this.props.onKeyPress(evt);
			}
		};

		handleFocusChange = (isInFocus) => {
			if (!isInFocus && this.props.error) {
				// Shake once
				this.setState({
					shake: true,
				});

				setTimeout(() => {
					this.setState({
						shake: false,
					});
				}, 500);
			}
			this.setState({
				isInFocus,
			});
		};

		handleBlur = (evt) => {
			if (!this.props.isWrappedComponent) {
				this.handleFocusChange(false);
			}
			if (this.props.onBlur) {
				this.props.onBlur(evt);
			}
		};

		getValidProps = (props) => {
			let validProps = {};
			if (
				isReactComponent(WrappedComponent) ||
				isReactComponent(WrappedComponent.WrappedComponent) || // since DateRangePicker is forwardRef component it might not have a prototype, use WrappedComponent property in this case
				options.type === 'TIME_RANGE' ||
				WrappedComponent.prototype === AsyncPaginate.prototype
			) {
				validProps = { ...props };
			} else {
				for (const prop in props) {
					if (prop.toLowerCase() === prop) {
						validProps[prop] = props[prop];
					} else if (INPUT_ALLOWED_ATTRIBUTES.includes(prop)) {
						validProps[prop] = props[prop];
					}
				}
			}
			if (options.type === 'DATE_RANGE') {
				validProps = {
					...validProps,
				};
				delete validProps.className;
				delete validProps.classNamePrefix;
				delete validProps.label;
				delete validProps.error;
				delete validProps.showErrorText;
				delete validProps.forwardedRef;
				return validProps;
			}

			validProps = {
				...validProps,
				className: undefined,
				error: undefined,
				required: undefined,
				onChange: undefined,
				onBlur: undefined,
				onFocus: undefined,
			};

			return validProps;
		};
		updateMaterialWrapperFocus = (value) => {
			this.setState({ wrapperInFocus: !value });
		};
		render() {
			const isReactSelect = WrappedComponent.prototype === ReactSelect.prototype;
			const isSelectEmpty = isReactSelect
				? !this.props.value ||
				  (typeof this.props.value === 'object' &&
						Object.keys(this.props.value).length === 0)
				: this.state.isEmpty;

			return (
				<div
					className={classNames(
						'material-like-text-field',
						this.props.className,
						{
							active: this.state.isInFocus || this.state.wrapperInFocus,
							error: this.props.error,
							small: this.props.smallHeight,
							inline: this.props.isInline,
							disabled: this.props.disabled,
							shake: this.state.shake,
						},
					)}
					onClick={() => {
						if (this.inputControl) {
							if (type === 'maskInput') {
								this.inputControl.current.getInputDOMNode().focus();
							} else if (
								this.inputControl &&
								!this.props.isWrappedComponent
							) {
								this.inputControl.current &&
									this.inputControl.current.focus &&
									this.inputControl.current.focus();
							}
						}
					}}
				>
					<div
						className={classNames('label', this.props.labelClassName, {
							'move-top':
								this.state.isInFocus ||
								!isSelectEmpty ||
								this.props.placeholder,
							'focused': this.state.isInFocus,
							'selected': !this.state.isInFocus && !isSelectEmpty,
							'move-left':
								(this.state.isInFocus ||
									!isSelectEmpty ||
									this.props.placeholder) &&
								this.props.labelLeftAligned,
						})}
						onClick={() => {
							if (this.inputControl) {
								if (type === 'maskInput') {
									this.inputControl.current.getInputDOMNode().focus();
								} else {
									this.inputControl.current &&
										this.inputControl.current.focus();
								}
							}
						}}
					>
						{isSelectEmpty && !this.state.isInFocus
							? this.props.label
							: this.props.focusedLabel || this.props.label}
						{this.props.required && (
							<span className="required-asterisk">*</span>
						)}
					</div>
					{options.type !== 'DATE_RANGE' && options.type !== 'TIME_RANGE' && (
						<div className="prefix">{this.props.prefix}</div>
					)}
					{options.type !== 'DATE_RANGE' && options.type !== 'TIME_RANGE' && (
						<WrappedComponent
							{...this.getValidProps(this.props)}
							aria-required={this.props.required || undefined}
							onFocus={(event) => {
								if (!this.props.isWrappedComponent) {
									this.handleFocusChange(true);
									if (this.props.onFocus) {
										this.props.onFocus(event);
									}
								}
							}}
							onKeyPress={this.handleKeyPress}
							onBlur={this.handleBlur}
							onChange={this.handleChange}
							defaultValue={this.props.defaultValue}
							type={this.props.type || 'text'}
							value={this.props.value}
							ref={this.inputControl}
							name={this.props.name}
							maxLength={this.props.maxLength}
							{...this.state.additionalProps}
							className={classNames(
								this.state.additionalProps?.className,
								this.props.inputClassName,
							)}
						></WrappedComponent>
					)}
					{options.type === 'DATE_RANGE' && (
						<WrappedComponent
							{...this.getValidProps(this.props)}
							minimumNights={0}
						/>
					)}
					{options.type === 'TIME_RANGE' && (
						<WrappedComponent
							{...this.getValidProps(this.props)}
							updateMaterialWrapperFocus={this.updateMaterialWrapperFocus}
						/>
					)}
					{!this.props.hideSuffixOnBlur ||
					this.state.isInFocus ||
					!this.state.isEmpty ? (
						<div ref={this.props.suffixRef} className="suffix">
							{this.props.suffix}
						</div>
					) : null}
					{typeof this.props.error === 'string' && this.props.showErrorText && (
						<div className="error-text">{this.props.error}</div>
					)}
				</div>
			);
		}
	}
	return React.forwardRef((props, ref) => {
		return <WrappedMaterialComponent {...props} forwardedRef={ref} />;
	});
}

export default MaterialWrapper;
